import { Component, Inject } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { distinctUntilChanged, switchMap } from 'rxjs/operators';
import { IRestaurantInfo } from '../../../interfaces/restaurant';
import { MenuStoreService } from '../../../services/menu/menu-store.service';
import { forkJoin, of } from 'rxjs';
import { IChainMenu, IChainMenuGroup } from '../../../interfaces/menu';
import { TranslateService } from '@ngx-translate/core';

type MenuLanguage = string | null;

type ChainMenuItemForm = {
  name: FormControl<string>;
  language: FormControl<MenuLanguage>;
  hidden: FormControl<boolean>;
  menuFile: FormControl<File | null>;
  iconFile: FormControl<File | null>;
  bannerFile: FormControl<File | null>;
};

type AddChainMenuForm = {
  happyHours: FormControl<boolean>;
  restaurants: FormControl<number[] | null>;
  toAll: FormControl<boolean>;
  menusFormArray: FormArray<FormGroup<ChainMenuItemForm>>;
};

@UntilDestroy()
@Component({
  selector: 'app-add-chain-menu',
  templateUrl: './add-chain-menu.component.html',
  styleUrls: ['./add-chain-menu.component.scss']
})
export class AddChainMenuComponent {

  public isLoading = false;
  public selected = 0;
  public languages = [
    { value: 'en-CA', viewValue: 'English' },
    { value: 'fr-CA', viewValue: 'French' },
    { value: null, viewValue: 'Not Set' },
  ];
  public form: FormGroup<AddChainMenuForm>;
  public menusFormArray: FormArray<FormGroup<ChainMenuItemForm>>;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: {
      chainId: number,
      happyHours: boolean,
      hidden?: boolean,
      allRestaurants: IRestaurantInfo[],
      mode?: 'create' | 'copy',
      source?: IChainMenuGroup
    },
    private menuState: MenuStoreService,
    private dialog: MatDialog,
    private translate: TranslateService,
  ) {
    const sourceMenus = this.data.source
      ? [this.data.source, ...this.data.source.translation]
      : [];
    const attachedToAll = this.data.source?.attachedToAll ?? true;
    const attachedRestaurantIds = this.data.source && !attachedToAll
      ? this.data.source.attachedRestaurantIds ?? []
      : null;

    this.menusFormArray = new FormArray<FormGroup<ChainMenuItemForm>>(
      sourceMenus.length
        ? sourceMenus.map(menu => this.createMenuFormGroup(menu))
        : [this.createMenuFormGroup()]
    );
    this.form = new FormGroup<AddChainMenuForm>({
      happyHours: new FormControl(this.data.source?.happyHours ?? this.data.happyHours, { nonNullable: true }),
      restaurants: new FormControl<number[] | null>({
        value: attachedRestaurantIds,
        disabled: attachedToAll
      }),
      toAll: new FormControl(attachedToAll, { nonNullable: true }),
      menusFormArray: this.menusFormArray,
    });

    this.form.controls.toAll.valueChanges.pipe(
      distinctUntilChanged(),
      untilDestroyed(this)
    ).subscribe((value: boolean) => {
      if (value) {
        this.form.controls.restaurants.reset({
          value: [],
          disabled: true,
        });
      } else {
        this.form.controls.restaurants.reset({
          value: null,
          disabled: false,
        });
      }
    });
  }

  get isCopyMode(): boolean {
    return this.data.mode === 'copy';
  }

  get menusControls(): Array<FormGroup<ChainMenuItemForm>> {
    return this.menusFormArray.controls;
  }

  get selectedLanguage(): MenuLanguage {
    return this.menusControls[this.selected].controls.language.value;
  }

  public isLanguageAvailable(language: MenuLanguage, currentIndex: number): boolean {
    const selectedLanguages = this.menusControls
      .map((menu, index) => index !== currentIndex ? menu.controls.language.value : null)
      .filter(lang => lang !== null);

    return !selectedLanguages.includes(language) && !(this.menusControls.length > 1 && language === null);
  }

  public addTranslate(): void {
    this.menusFormArray.push(this.createMenuFormGroup(undefined, true));

    this.selected = this.menusFormArray.length - 1;
  }

  public removeTranslate(index: number): void {
    this.menusFormArray.removeAt(index);
    this.selected = index;
  }

  public onSubmit(): void {
    if (this.form.invalid || this.isLoading) {
      return;
    }

    this.isLoading = true;

    const [menuOriginalData, ...menuTranslatedData] = this.menusControls;
    const formValue = this.form.getRawValue();
    const selectedRestaurants = formValue.toAll ? [] : formValue.restaurants;

    const addChainMenu = (menuControl: FormGroup<ChainMenuItemForm>, originalId: number | null) => {
      const menuData = menuControl.getRawValue();

      if (!menuData.menuFile) {
        throw new Error('Menu file is required');
      }

      return this.menuState.addChainMenu(
        this.data.chainId,
        menuData.name,
        menuData.language,
        formValue.happyHours,
        menuData.hidden,
        menuData.menuFile,
        selectedRestaurants,
        originalId,
        menuData.iconFile || undefined,
        menuData.bannerFile || undefined
      );
    };

    addChainMenu(menuOriginalData, null).pipe(
      switchMap((chainMenu) => {
        if (menuTranslatedData.length > 0) {
          return forkJoin(
            menuTranslatedData.map((translate) => addChainMenu(translate, chainMenu.id))
          );
        }
        return of(chainMenu);
      }),
      untilDestroyed(this)
    ).subscribe({
      next: () => {
        this.isLoading = false;
        this.dialog.closeAll();
      },
      error: () => {
        this.isLoading = false;
        this.dialog.closeAll();
      },
    });
  }

  private createMenuFormGroup(source?: IChainMenu, languageRequired = false): FormGroup<ChainMenuItemForm> {
    const copySuffix = this.translate.instant('pages.menu.modal.copySuffix');

    return new FormGroup<ChainMenuItemForm>({
      name: new FormControl(source ? `${source.name} (${copySuffix})` : '', {
        nonNullable: true,
        validators: [Validators.required, Validators.minLength(3)]
      }),
      language: new FormControl<MenuLanguage>(source?.language ?? null, languageRequired ? [Validators.required] : []),
      hidden: new FormControl(source?.hidden ?? false, { nonNullable: true }),
      menuFile: new FormControl<File | null>(null, [Validators.required]),
      iconFile: new FormControl<File | null>(null),
      bannerFile: new FormControl<File | null>(null)
    });
  }
}
